/**
 * Scheduling — pick a job and a week, then build it.
 *
 * Slice 4b. Around the grid sit the things that are not the grid: choosing a
 * job, moving between weeks, building a week from the Master schedule, the gap
 * list, and the empty states a system with zero jobs actually hits on day one.
 * Every one of those is a designed screen rather than a spinner.
 *
 * The vocabulary is fixed and matches what managers already say:
 *   Master schedule  — the recurring shifts on a job
 *   Working schedule — the week you are editing
 *
 * Rates: whether a pay rate or a bill rate appears on a shift row is decided
 * by the server, which sends the field only to a viewer entitled to it. This
 * page renders what arrived. It does not gate on a role and it does not gate
 * on a capability read in the browser, because a field that reached the
 * browser has already leaked.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { schedulingAPI, hoursTypesAPI } from '../lib/api';
import { useCap } from '../components/auth/RequireCap';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  CalendarDays, Check, ChevronLeft, ChevronRight, Cloud, Copy, LayoutGrid, Loader2,
  Sparkles, UserX, Briefcase, FileStack,
} from 'lucide-react';
import { useWeekEditor } from '../components/scheduling/useWeekEditor';
import { WeekGrid } from '../components/scheduling/WeekGrid';
import { WeekCards } from '../components/scheduling/WeekCards';
import { WeekApprovalBar } from '../components/scheduling/WeekApprovalBar';
import { OpenPostsPanel } from '../components/scheduling/OpenPostsPanel';
import OfficerWeekView from '../components/scheduling/OfficerWeekView';

const iso = (d) => d.toISOString().slice(0, 10);
const sundayOf = (value) => {
  const d = new Date(`${value}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return iso(d);
};
const shiftWeeks = (weekStart, n) => {
  const d = new Date(`${weekStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n * 7);
  return iso(d);
};
const prettyWeek = (weekStart) => {
  const s = new Date(`${weekStart}T00:00:00Z`);
  const e = new Date(s);
  e.setUTCDate(e.getUTCDate() + 6);
  const f = (d) => d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
  return `${f(s)} – ${f(e)}, ${e.getUTCFullYear()}`;
};

/** Every empty state on this screen shares one shape, so none of them reads
 *  like a failure. */
const EmptyState = ({ icon: Icon, title, body, action, testId }) => (
  <div className="rounded-xl border border-line bg-surface-sunken px-6 py-12 text-center"
    data-testid={testId}>
    <Icon className="h-9 w-9 mx-auto text-fg-muted" />
    <h2 className="text-base md:text-lg font-medium text-fg mt-4">{title}</h2>
    <p className="text-sm text-fg-muted mt-2 max-w-lg mx-auto leading-relaxed">{body}</p>
    {action && <div className="mt-5">{action}</div>}
  </div>
);

function GenerateDialog({ open, onOpenChange, readiness, weeks, onGenerate }) {
  const [mode, setMode] = useState('templates');
  const [sourceWeek, setSourceWeek] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setMode(readiness?.can_generate_from_templates ? 'templates'
      : readiness?.can_copy_last_week ? 'copy_last_week' : 'blank');
    setSourceWeek('');
  }, [open, readiness]);

  const run = async () => {
    setBusy(true);
    try {
      await onGenerate({ mode, source_week_start_date: sourceWeek || null });
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  const Option = ({ value, title, body, disabled, testId }) => (
    <button type="button" disabled={disabled} onClick={() => setMode(value)}
      data-testid={testId}
      className={`w-full text-left rounded-lg border p-4 transition-colors ${
        mode === value ? 'border-primary bg-primary/5' : 'border-line bg-surface-sunken'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/60'}`}>
      <p className="text-sm font-medium text-fg">{title}</p>
      <p className="text-xs text-fg-muted mt-1 leading-relaxed">{body}</p>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="generate-week-dialog">
        <DialogHeader>
          <DialogTitle>Build this working schedule</DialogTitle>
          <DialogDescription>
            Nothing is written until you pick one.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Option value="templates" testId="generate-mode-templates"
            disabled={!readiness?.can_generate_from_templates}
            title="From the Master schedule"
            body={readiness?.can_generate_from_templates
              ? `${readiness.active_template_count} recurring shift${
                  readiness.active_template_count === 1 ? '' : 's'} on this job. Times, hours
                 types and assigned officers all come with them.`
              : `This job has no Master schedule yet — add the recurring shifts on the job's
                 Shifts tab.`} />
          <Option value="copy_last_week" testId="generate-mode-copy-last"
            disabled={!readiness?.can_copy_last_week}
            title="Copy last week"
            body={readiness?.previous_week
              ? `Reproduces the week of ${readiness.previous_week.week_start_date}, officers
                 included, dates moved forward seven days.`
              : 'There is no earlier week on this job yet.'} />
          <Option value="copy_from_week" testId="generate-mode-copy-from"
            disabled={!weeks.length}
            title="Copy from a specific week"
            body="What a rotating pattern needs — pick any week this job has already run." />
          {mode === 'copy_from_week' && (
            <Select value={sourceWeek} onValueChange={setSourceWeek}>
              <SelectTrigger data-testid="generate-source-week">
                <SelectValue placeholder="Which week?" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {weeks.map(w => (
                  <SelectItem key={w.id} value={w.week_start_date}>
                    {prettyWeek(w.week_start_date)} · {w.shift_count} shifts
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Option value="blank" testId="generate-mode-blank" title="Start blank"
            body="An empty week you fill in yourself." />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={run} disabled={busy || (mode === 'copy_from_week' && !sourceWeek)}
            data-testid="generate-week-confirm">
            {busy ? 'Building…' : 'Build it'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Autosave, said once, quietly. Managers do not press save. */
function SaveState({ saving, pending, lastSavedAt }) {
  if (saving || pending) {
    return (
      <span className="text-xs text-fg-muted flex items-center gap-1.5" data-testid="autosave-saving">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Saving{pending ? ` ${pending} more` : ''}…
      </span>
    );
  }
  if (lastSavedAt) {
    return (
      <span className="text-xs text-fg-muted flex items-center gap-1.5" data-testid="autosave-saved">
        <Check className="h-3.5 w-3.5" />
        Saved {lastSavedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
      </span>
    );
  }
  return (
    <span className="text-xs text-fg-muted flex items-center gap-1.5" data-testid="autosave-idle">
      <Cloud className="h-3.5 w-3.5" /> Saves as you go
    </span>
  );
}

export default function Scheduling() {
  const { jobId: routeJobId, weekStart: routeWeekStart, employeeId } = useParams();
  const navigate = useNavigate();
  const canAdd = useCap('scheduling', 'add');
  const canEdit = useCap('scheduling', 'edit');
  const canApprove = useCap('scheduling', 'approve');
  // The grid gate. Admin only in v4.23.0 by release decision; grantable to one
  // person, or to a role from the Permissions screen, for the December
  // rollout. Without it this screen is exactly what it was in Slice 1-3 - the
  // week as seven read-only day cards - not an error and not an empty state.
  // The server refuses the batch write too, so this is a courtesy, not the
  // enforcement.
  const hasGrid = useCap('scheduling_grid', 'view');

  const [jobs, setJobs] = useState([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [weeks, setWeeks] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [tab, setTab] = useState('grid');
  const [hoursTypes, setHoursTypes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [weekState, setWeekState] = useState(null);

  const jobId = routeJobId || '';
  const weekStart = useMemo(
    () => sundayOf(routeWeekStart || iso(new Date())), [routeWeekStart]);

  const editor = useWeekEditor({ jobId, weekStart });
  const { data: weekData, loading, saving, pending, blocked, lastSavedAt } = editor;

  useEffect(() => {
    schedulingAPI.jobs({ scheduling_enabled: true })
      .then(({ data }) => setJobs(data || []))
      .catch(() => toast.error('Could not load the jobs enabled for scheduling'))
      .finally(() => setJobsLoaded(true));
    hoursTypesAPI.list().then(({ data }) => setHoursTypes(data || [])).catch(() => {});
    schedulingAPI.callOffReasons()
      .then(({ data }) => setReasons(data.reasons || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!jobId) { setWeeks([]); return; }
    schedulingAPI.jobWeeks(jobId).then(({ data }) => setWeeks(data || [])).catch(() => setWeeks([]));
  }, [jobId, weekData?.week?.id]);

  // Phase C1: the week's approval state (status, submitter/approver, lock,
  // self-approved marker, payroll flag). Only meaningful behind the grid gate.
  const weekId = weekData?.week?.id;
  const refreshWeekState = useCallback(() => {
    if (!weekId || !hasGrid) { setWeekState(null); return; }
    schedulingAPI.weekState(weekId)
      .then(({ data }) => setWeekState(data)).catch(() => setWeekState(null));
  }, [weekId, hasGrid]);
  useEffect(() => { refreshWeekState(); }, [refreshWeekState, weekData?.week?.status]);

  useEffect(() => {
    if (editor.error) {
      toast.error(editor.error);
      editor.setError(null);
    }
  }, [editor.error, editor]);

  const go = (id, ws) => navigate(`/scheduling/${id}/${ws}`);

  const generate = async ({ mode, source_week_start_date }) => {
    try {
      const { data } = await schedulingAPI.generateWeek({
        job_id: jobId, week_start_date: weekStart, mode, source_week_start_date,
      });
      toast.success(data.shifts_created
        ? `${data.shifts_created} shift${data.shifts_created === 1 ? '' : 's'} built`
        : 'Working schedule created');
      editor.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build the week');
      throw e;
    }
  };

  const resolveBlocked = useCallback((item, answer, all) => {
    const items = all ? blocked.filter((b) => b.code === item.code) : [item];
    editor.enqueueMany(items.map((b) => {
      const change = { ...b.change };
      if (b.code === 'qualification_acknowledgement_required') {
        change.acknowledge = { ...(change.acknowledge || {}), qualification: true };
      } else if (b.code === 'overtime_acknowledgement_required') {
        change.acknowledge = { ...(change.acknowledge || {}), overtime: true };
      } else if (b.code === 'call_off_reason_required' || b.code === 'call_off_note_required') {
        change.call_off = { reason: answer.reason, note: answer.note };
      }
      return { key: b.key, change };
    }));
    editor.removeBlocked(items.map((b) => b.key));
  }, [blocked, editor]);

  const cancelBlocked = useCallback(() => {
    // Only the one being asked about is dropped: the rest are still questions.
    editor.removeBlocked([blocked[0]?.key]);
    editor.reload();
  }, [blocked, editor]);

  const setHoliday = useCallback(async (shift, action) => {
    try {
      await schedulingAPI.setHoliday(shift.id, action);
      editor.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record that');
    }
  }, [editor]);

  // One officer's week across every job lives on its own route.
  if (employeeId) return <OfficerWeekView />;

  const job = weekData?.job;
  const readiness = weekData?.readiness;
  const shiftDays = weekData?.shift_days || [];
  const openPosts = shiftDays.filter(s => s.is_open_post || !s.employee_id).length;
  const totals = weekData?.totals;

  // ── no jobs at all: the state production is in today ──
  if (jobsLoaded && !jobs.length) {
    return (
      <div className="max-w-5xl mx-auto p-4 md:p-6" data-testid="scheduling-page">
        <h1 className="text-3xl sm:text-4xl font-semibold text-fg tracking-tight">Scheduling</h1>
        <div className="mt-8">
          <EmptyState icon={Briefcase} testId="scheduling-no-jobs"
            title="No jobs are enabled for scheduling yet"
            body="A job appears here once it is switched on for scheduling — either on the
                  job's own Scheduling tab, or automatically when a work order for it is
                  approved. Nothing is missing; there is simply nothing to schedule yet." />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[112rem] mx-auto p-4 md:p-6 space-y-6" data-testid="scheduling-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-fg tracking-tight">Scheduling</h1>
          <p className="text-sm text-fg-muted mt-1.5">
            Working schedule, Sunday through Saturday.
          </p>
        </div>
        {/* min-w-0 on both, because the trigger is whitespace-nowrap and a long
            job label gave this block an intrinsic width wider than a 390px
            phone - the page scrolled sideways before the label would truncate.
            16rem is a minimum from sm up, not on a phone. */}
        <div className="flex items-end gap-3 flex-wrap w-full sm:w-auto min-w-0">
          <div className="w-full min-w-0 sm:w-auto sm:min-w-[16rem]">
            <Select value={jobId} onValueChange={(v) => go(v, weekStart)}>
              <SelectTrigger data-testid="scheduling-job-select">
                <SelectValue placeholder="Pick a job…" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {jobs.map(j => (
                  <SelectItem key={j.id} value={j.id}>
                    {j.job_number} · {j.job_description || j.client_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {jobId && (
            <div className="flex items-center gap-1 rounded-lg border border-line bg-surface-sunken p-1">
              <Button variant="ghost" size="sm" onClick={() => go(jobId, shiftWeeks(weekStart, -1))}
                data-testid="scheduling-week-prev" aria-label="Previous week">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-fg px-2 whitespace-nowrap tabular-nums"
                data-testid="scheduling-week-label">
                {prettyWeek(weekStart)}
              </span>
              <Button variant="ghost" size="sm" onClick={() => go(jobId, shiftWeeks(weekStart, 1))}
                data-testid="scheduling-week-next" aria-label="Next week">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {!jobId ? (
        <EmptyState icon={CalendarDays} testId="scheduling-pick-a-job"
          title="Pick a job to see its week"
          body="Choose a job above. You will get its whole week at once, Sunday through
                Saturday, with every recurring shift already in place." />
      ) : loading ? (
        <p className="text-sm text-fg-muted" data-testid="scheduling-loading">Loading…</p>
      ) : !weekData ? null : !readiness?.post_count ? (
        <EmptyState icon={LayoutGrid} testId="scheduling-no-posts"
          title={`${job?.job_number} has no posts yet`}
          body="A schedule is built out of posts — the positions this job covers. Add them on
                the job's Posts tab first; each post carries the billing item its hours bill
                against."
          action={<Button variant="outline" onClick={() => navigate('/jobs')}
            data-testid="scheduling-go-to-posts">Open Jobs</Button>} />
      ) : !weekData.exists ? (
        <EmptyState icon={Sparkles} testId="scheduling-week-not-built"
          title={`Nothing built for ${prettyWeek(weekStart)} yet`}
          body={readiness?.can_generate_from_templates
            ? `This job has a Master schedule with ${readiness.active_template_count} recurring
               shifts. Build the week from it and every shift arrives with its times, hours
               type and officer already set.`
            : readiness?.can_copy_last_week
              ? `No Master schedule on this job yet — but last week can be copied forward in
                 one action, officers included.`
              : `No Master schedule and no earlier week to copy. Add the recurring shifts on
                 the job's Shifts tab, or start this week blank.`}
          action={canAdd
            ? <Button onClick={() => setGenOpen(true)} data-testid="scheduling-build-week">
                <Sparkles className="h-4 w-4 mr-1.5" /> Build this week
              </Button>
            : <p className="text-xs text-fg-muted">You have view access to schedules.</p>} />
      ) : (
        <>
          {hasGrid ? (
            <WeekApprovalBar week={weekData.week} state={weekState}
              canApprove={canApprove}
              onChanged={() => { editor.reload(); refreshWeekState(); }} />
          ) : (
            <Badge variant="outline" data-testid="scheduling-week-status">
              {weekData.week?.status === 'draft' ? 'Draft' : weekData.week?.status}
            </Badge>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-fg-muted" data-testid="scheduling-shift-count">
              {shiftDays.length} shift{shiftDays.length === 1 ? '' : 's'}
            </span>
            {totals?.site_hours != null && (
              <span className="text-sm text-fg-muted tabular-nums" data-testid="scheduling-site-hours">
                {totals.site_hours}h this week
              </span>
            )}
            {openPosts > 0 && (
              <span className="text-sm text-fg-muted flex items-center gap-1.5"
                data-testid="scheduling-open-posts">
                <UserX className="h-3.5 w-3.5" /> {openPosts} unfilled
              </span>
            )}
            {totals?.contracted_weekly_hours != null && (
              <span className="text-sm text-fg-muted tabular-nums"
                data-testid="scheduling-contract-variance">
                Contracted {totals.contracted_weekly_hours}h
                {totals.variance_vs_contracted != null
                  && ` · ${totals.variance_vs_contracted > 0 ? '+' : ''}${totals.variance_vs_contracted}h`}
              </span>
            )}
            {job && job.ot_billable === false && (
              <span className="text-sm text-fg-muted" data-testid="scheduling-ot-not-billable">
                This job does not bill overtime
              </span>
            )}
            <div className="ml-auto flex items-center gap-3">
              <SaveState saving={saving} pending={pending} lastSavedAt={lastSavedAt} />
              {canAdd && (
                <Button variant="outline" size="sm" onClick={() => setGenOpen(true)}
                  data-testid="scheduling-regenerate">
                  <Copy className="h-4 w-4 mr-1.5" /> Add or rebuild shifts
                </Button>
              )}
            </div>
          </div>

          {hasGrid && (
            <div className="flex items-center gap-1 rounded-lg border border-line
                            bg-surface-sunken p-1 w-fit">
              {[['grid', 'Working schedule'], ['open', 'Unfilled posts']].map(([v, label]) => (
                <button key={v} type="button" onClick={() => setTab(v)}
                  data-testid={`scheduling-tab-${v}`}
                  className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                    tab === v ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted hover:text-fg'}`}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {!hasGrid ? (
            <WeekCards dates={weekData.dates || []} posts={weekData.posts || []}
              shifts={shiftDays} />
          ) : tab === 'grid' ? (
            <WeekGrid weekId={weekData.week?.id} weekStart={weekStart} job={job}
              posts={weekData.posts || []} dates={weekData.dates || []} shifts={shiftDays}
              totals={totals} employees={weekData.employees || []} hoursTypes={hoursTypes}
              callOffReasons={reasons} canEdit={canEdit && (weekData.week?.status || 'draft') === 'draft'}
              showPay={shiftDays.some((s) => 'pay_rate_ten_thousandths' in s)}
              showBill={shiftDays.some((s) => 'bill_rate_ten_thousandths' in s
                || 'bill_rate_status' in s)}
              blocked={blocked}
              onCommit={editor.enqueueMany}
              onResolveBlocked={resolveBlocked}
              onCancelBlocked={cancelBlocked}
              onHoliday={setHoliday}
              onOpenOfficer={(id) => navigate(`/scheduling/officer/${id}/${weekStart}`)} />
          ) : (
            <OpenPostsPanel weekStart={weekStart} jobId={jobId}
              onOpenWeek={(id, date) => go(id, sundayOf(date))} />
          )}
        </>
      )}

      {weeks.length > 0 && jobId && (
        <div className="pt-2">
          <p className="text-xs uppercase tracking-wide text-fg-muted flex items-center gap-1.5">
            <FileStack className="h-3.5 w-3.5" /> Weeks on this job
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {weeks.slice(0, 12).map(w => (
              <button key={w.id} onClick={() => go(jobId, w.week_start_date)}
                data-testid={`scheduling-week-chip-${w.week_start_date}`}
                className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                  w.week_start_date === weekStart
                    ? 'border-primary text-fg bg-primary/5'
                    : 'border-line text-fg-muted hover:text-fg'}`}>
                {prettyWeek(w.week_start_date)} · {w.shift_count}
              </button>
            ))}
          </div>
        </div>
      )}

      <GenerateDialog open={genOpen} onOpenChange={setGenOpen} readiness={readiness}
        weeks={weeks} onGenerate={generate} />
    </div>
  );
}
